import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';

import { DoubleShift } from "../../types/Shift";

// Note: every shift date in this slice is a bare calendar date. Its writers do not all agree - the
// date picker sends toISODate(), while the login bootstrapper and refreshLatestShift pass the API's
// string through, which can carry a time - and two things depend on the format rather than the value.
// Offline, "is this the latest shift" is a string comparison, and it is the only thing that keeps a
// shift editable, so a mismatch locks the crew out of the shift they are working after a reload.
// computeShiftId also appends "T00:00:00Z", which yields an invalid date (a NaN ShiftId on every
// queued change) if a time is already present. Normalising on the way in settles both.
function toIsoDate(value: string): string;
function toIsoDate(value: string | null): string | null;
function toIsoDate(value: string | null): string | null {
  if (!value) return value;
  return DateTime.fromISO(value).toISODate() ?? value;
}

interface ShiftState {
  selectedDate: string;
  selectedShift: DoubleShift;
  // Note: the system's latest shift (latest tblShift record), fetched by ShiftLoginBootstrapper.
  // Persisted so the offline complete-shift flow can tell whether the selected shift is the
  // latest without a server call or any reliance on DateTime.now().
  latestShiftDate: string | null;
  latestShift: DoubleShift | null;
  // Note: the shift a completion queued while offline has provisionally created. It does not exist
  // on the server yet, so nothing can report its lock state; it is treated as editable offline so
  // the crew can work the shift they just moved into. Cleared once the completion syncs and the
  // server becomes the authority again.
  provisionalShiftDate: string | null;
  provisionalShift: DoubleShift | null;
  shiftStartTime: string | null; // ISO UTC based date
  shiftEndTime: string | null;
  isLocked: boolean;
  canToggleLock: boolean;
  isPastAutoLockCutOff: boolean | null;
  // Note: whether the selected shift exists on the server. A shift that does not is reported as
  // locked, so without this the banner tells the crew an administrator has locked a shift they have
  // just created offline, rather than that it has not synced yet.
  shiftExists: boolean;
  isShiftEditibilityDataLoading: boolean;
  shiftEditabilityFetched: boolean;
}

const initialState: ShiftState = {
  selectedDate: toIsoDate(localStorage.getItem("redux_selectedDate")) || DateTime.now().toISODate(),
  selectedShift: (localStorage.getItem("redux_selectedShift") as DoubleShift) || 'DAY',
  latestShiftDate: toIsoDate(localStorage.getItem("redux_latestShiftDate")),
  latestShift: (localStorage.getItem("redux_latestShift") as DoubleShift) || null,
  provisionalShiftDate: toIsoDate(localStorage.getItem("redux_provisionalShiftDate")),
  provisionalShift: (localStorage.getItem("redux_provisionalShift") as DoubleShift) || null,
  shiftStartTime: null,
  shiftEndTime: null,
  isLocked: false,
  canToggleLock: false,
  isPastAutoLockCutOff: null,
  shiftExists: true,
  isShiftEditibilityDataLoading: false,
  shiftEditabilityFetched: false,
};

const selectedShiftSlice = createSlice({
  name: 'selectedShift',
  initialState,
  reducers: {
    setSelectedDate(state, action: PayloadAction<string>) {
      state.selectedDate = toIsoDate(action.payload);
      localStorage.setItem("redux_selectedDate", state.selectedDate);
    },
    setSelectedShift(state, action: PayloadAction<DoubleShift>) {
      state.selectedShift = action.payload;
      localStorage.setItem("redux_selectedShift", action.payload);
    },
    setLatestShift(state, action: PayloadAction<{ date: string; shift: DoubleShift }>) {
      state.latestShiftDate = toIsoDate(action.payload.date);
      state.latestShift = action.payload.shift;
      localStorage.setItem("redux_latestShiftDate", state.latestShiftDate);
      localStorage.setItem("redux_latestShift", action.payload.shift);
    },
    setProvisionalShift(state, action: PayloadAction<{ date: string; shift: DoubleShift }>) {
      state.provisionalShiftDate = toIsoDate(action.payload.date);
      state.provisionalShift = action.payload.shift;
      localStorage.setItem("redux_provisionalShiftDate", state.provisionalShiftDate);
      localStorage.setItem("redux_provisionalShift", action.payload.shift);
    },
    clearProvisionalShift(state) {
      state.provisionalShiftDate = null;
      state.provisionalShift = null;
      localStorage.removeItem("redux_provisionalShiftDate");
      localStorage.removeItem("redux_provisionalShift");
    },
    setShiftBoundaries(state, action: PayloadAction<{ start: string; end: string }>) {
      state.shiftStartTime = action.payload.start;
      state.shiftEndTime = action.payload.end;
    },
    setShiftEditability(state, action: PayloadAction<{ isLocked: boolean; canToggleLock: boolean; isPastAutoLockCutOff: boolean | null; exists?: boolean }>) {
      state.isLocked = action.payload.isLocked;
      state.canToggleLock = action.payload.canToggleLock;
      state.isPastAutoLockCutOff = action.payload.isPastAutoLockCutOff;
      state.shiftExists = action.payload.exists ?? true;
      state.shiftEditabilityFetched = true;
    },
    setShiftEditabilityDataLoading(state, action: PayloadAction<boolean>) {
      state.isShiftEditibilityDataLoading = action.payload;
    }
  },
});

export const {
  setSelectedDate,
  setSelectedShift,
  setLatestShift,
  setProvisionalShift,
  clearProvisionalShift,
  setShiftBoundaries,
  setShiftEditability,
  setShiftEditabilityDataLoading
} = selectedShiftSlice.actions;
export default selectedShiftSlice.reducer;
